import { Component, ErrorInfo, ReactNode } from "react";
import { Card } from "../Card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  title?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary
 *
 * React error boundary component for graceful error handling.
 * Catches JavaScript errors in child components and displays a fallback UI with retry option.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error Boundary caught an error:", error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card>
          {this.props.title && <Card.Header title={this.props.title} />}
          <Card.Content>
            <div className="flex flex-col items-center justify-center gap-4" data-testid="error-boundary">
              <p className="text-red-500">{this.state.error?.message || "Something went wrong"}</p>
              <button className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600" onClick={this.handleRetry}>
                Retry
              </button>
            </div>
          </Card.Content>
        </Card>
      );
    }

    return this.props.children;
  }
}
